import { useState } from "react";
import {
  Box as MuiBox,
  Button as MuiButton,
  CircularProgress as MuiCircularProgress,
  Stack as MuiStack,
  Tooltip as MuiTooltip,
  Typography as MuiTypography,
} from "@mui/material";
import { ImportExport as MuiImportExportIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { LOADER_TYPES } from "app/services/OISService/constants";
import { useOIS } from "app/services/OISService/OISProvider";
import EProtocolTable from "./EProtocolTable";
import UploadToOnCore from "./UploadToOnCore";

const DatePickers = () => {
  const { t } = useTranslation();
  const [startDateFrom, setStartDateFrom] = useState(null);
  const [endDateTo, setEndDateTo] = useState(null);
  const {
    isLoading,
    uploadLink,
    eProtocolRecords,
    searchEProtocol,
    downloadFile,
  } = useOIS();

  const hasResults = eProtocolRecords?.irbResources?.length > 0;
  const showNoDataMessage =
    !hasResults &&
    startDateFrom &&
    endDateTo &&
    eProtocolRecords?.length === undefined;

  return (
    <MuiBox>
      <MuiStack
        direction="row"
        sx={{
          alignItems: "center",
          gap: 1.75,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t("eProtocol.label.startDate")}
            value={startDateFrom}
            onChange={(date) => setStartDateFrom(date)}
            slotProps={{
              openPickerButton: {
                sx: {
                  mr: "0px",
                  "& .MuiSvgIcon-root": { fontSize: "22px" },
                },
              },
              textField: {
                size: "small",
              },
            }}
            sx={{
              "& input": { fontSize: "14px" },
              "& label": { fontSize: "14px" },
            }}
          />
          <DatePicker
            label={t("eProtocol.label.endDate")}
            value={endDateTo}
            onChange={(date) => setEndDateTo(date)}
            slotProps={{
              openPickerButton: {
                sx: {
                  mr: "0px",
                  "& .MuiSvgIcon-root": { fontSize: "22px" },
                },
              },
              textField: {
                size: "small",
              },
            }}
            sx={{
              "& input": { fontSize: "14px" },
              "& label": { fontSize: "14px" },
            }}
          />
        </LocalizationProvider>

        {!isLoading[LOADER_TYPES.EPROTOCOL_ACTIVITY] ? (
          <MuiTooltip
            title={t("eProtocol.tooltip.extract")}
            placement="top"
            arrow
          >
            <MuiButton
              variant="contained"
              onClick={() => searchEProtocol(startDateFrom, endDateTo)}
              sx={{
                backgroundColor: "#800000",
              }}
            >
              {t("buttons.caps.extract")}
            </MuiButton>
          </MuiTooltip>
        ) : (
          <MuiCircularProgress size="25.7143px" />
        )}
      </MuiStack>

      {hasResults && (
        <MuiStack
          sx={{
            gap: 1.75,
            marginTop: 2,
          }}
        >
          <MuiStack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <MuiTypography
              variant="h2"
              sx={{ fontSize: "21px", fontWeight: 100 }}
            >
              {t("eProtocol.table.title")}
            </MuiTypography>
            <MuiTooltip
              title={t("eProtocol.tooltip.export")}
              placement="top"
              arrow
            >
              <MuiButton
                size="small"
                variant="contained"
                startIcon={<MuiImportExportIcon />}
                disabled={eProtocolRecords.length === 0 ? true : false}
                onClick={(event) => {
                  event.preventDefault();
                  downloadFile(eProtocolRecords._links.download.href);
                }}
              >
                {t("buttons.caps.export")}
              </MuiButton>
            </MuiTooltip>
          </MuiStack>

          <MuiStack
            sx={{
              gap: 1.75,
            }}
          >
            <EProtocolTable />
            <UploadToOnCore
              csvType={t("csvTypes.eProtocol")}
              uploadLink={uploadLink}
            />
          </MuiStack>
        </MuiStack>
      )}

      {showNoDataMessage && (
        <MuiTypography color="error" sx={{ fontSize: "14px", fontWeight: 700 }}>
          {t("eProtocol.table.noRows")}
        </MuiTypography>
      )}
    </MuiBox>
  );
};

export default DatePickers;
